import React from 'react'
import styled from 'styled-components'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { line, radius, font, surface } from 'features/dashboard/tokens'
import { PRIORITY_LEVELS, PRIORITY_META, priorityKey } from 'features/taskManagement/taskPriority'

const TasksByPriorityCard = ({ byPriority = [], onOpenPriority, rangeLabel }) => {
  const counts = {}
  ;(byPriority || []).forEach(b => {
    if (!b || !b.key) return
    const key = priorityKey(b.key)
    counts[key] = (counts[key] || 0) + (b.count || 0)
  })
  const total = PRIORITY_LEVELS.reduce((s, k) => s + (counts[k] || 0), 0)
  const unset = (byPriority || []).filter(b => b && b.key && !PRIORITY_LEVELS.includes(priorityKey(b.key))).reduce((s, b) => s + (b.count || 0), 0)

  if (total === 0) {
    return <Empty>No tasks with a priority set{rangeLabel ? ` for ${rangeLabel}` : ''}. Set a priority when you create or edit a task to see the split here.</Empty>
  }

  return (
    <Wrap>
      <Intro>
        How urgent each task is, from critical down to low{rangeLabel ? `, for tasks due ${rangeLabel}` : ''}.
        {onOpenPriority ? ' Click a level to see those tasks.' : ''}
      </Intro>
      <Bar role='img' aria-label={PRIORITY_LEVELS.map(k => `${PRIORITY_META[k].label}: ${counts[k] || 0}`).join(', ')}>
        {PRIORITY_LEVELS.map(k => (counts[k] > 0
          ? <BarSeg key={k} style={{ width: `${(counts[k] / total) * 100}%`, background: PRIORITY_META[k].fg }} title={`${PRIORITY_META[k].label}: ${counts[k]}`} />
          : null))}
      </Bar>
      <List>
        {PRIORITY_LEVELS.map(k => {
          const meta = PRIORITY_META[k]
          const n = counts[k] || 0
          const pct = total ? Math.round((n / total) * 100) : 0
          const content = (
            <>
              <Dot style={{ background: meta.fg }} />
              <RowText>
                <RowLabel>{meta.label}</RowLabel>
                <RowHint>{meta.hint}</RowHint>
              </RowText>
              <RowCount>
                {n}
                <RowPct>{n > 0 && pct === 0 ? '<1' : pct}%</RowPct>
              </RowCount>
              {onOpenPriority && <ChevronRightRoundedIcon style={{ fontSize: 20, color: '#98a2b3' }} aria-hidden='true' />}
            </>
          )
          return onOpenPriority ? (
            <Row
              key={k}
              as='button'
              type='button'
              $clickable
              onClick={() => onOpenPriority(k)}
              aria-label={`${meta.label} priority: ${n} tasks. Open these tasks.`}
            >
              {content}
            </Row>
          ) : (
            <Row key={k}>{content}</Row>
          )
        })}
      </List>
      {unset > 0 && <Foot>{unset} more {unset === 1 ? 'task has' : 'tasks have'} no priority set and {unset === 1 ? 'is' : 'are'} not counted above.</Foot>}
    </Wrap>
  )
}

export default TasksByPriorityCard

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 12px;
`

const Bar = styled.div`
  display: flex;
  height: 10px;
  border-radius: ${radius.pill};
  overflow: hidden;
  background: ${surface.wash};
  border: 1px solid ${line.base};
`

const BarSeg = styled.div`
  height: 100%;
`

const List = styled.div`
  display: flex; flex-direction: column;
`

const Row = styled.div`
  font: inherit; width: 100%; text-align: left; background: none; border: none;
  display: flex; align-items: center; gap: 10px;
  min-height: 48px; padding: 8px 6px; border-radius: ${radius.sm};
  border-bottom: 1px solid ${surface.wash};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover { background: ${({ $clickable }) => ($clickable ? surface.wash : 'transparent')}; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 1px; }
  &:last-child { border-bottom: none; }
`

const Intro = styled.p`
  margin: 0; font-size: 13px; line-height: 1.45; color: ${({ theme }) => theme.colors.grey[800]};
`

const RowText = styled.span`
  display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0;
`

const RowHint = styled.span`
  font-size: 12.5px; line-height: 1.35; color: #667085;
`

const RowPct = styled.span`
  display: block; font-size: 11.5px; font-weight: 700; color: #667085; text-align: right;
`

const Foot = styled.p`
  margin: 0; font-size: 12.5px; color: #667085;
`

const Dot = styled.span`
  width: 10px; height: 10px; border-radius: 3px; flex: 0 0 auto;
`

const RowLabel = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const RowCount = styled.span`
  text-align: right;
  font-size: ${font.emphasis};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
  font-variant-numeric: tabular-nums;
`

const Empty = styled.div`
  padding: 24px; text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
`
